import { useCallback, useRef, useState } from 'react';
import { AIFeature, StreamingEvent } from 'enums';
import { ServerPushEvent, streamingText, useServerPushNotifications } from 'utils';
import { useUnsupportedFilesNotification } from './useUnsupportedFilesNotification';

/**
 * This hook is used as a React state for AI features response, that listens and updates the state (actual content)
 * with the server streaming messages.
 */
export const useStateWithServerStreaming = (feature: AIFeature): [string, boolean, () => void] => {
  const [value, setValue] = useState('');
  const isStreaming = useRef<boolean>(false);
  const activeStreams = useRef<Record<string, string>>({});

  const { showNotification } = useUnsupportedFilesNotification();

  const deltaMessageHandler = useCallback(
    ({ event, context, message, messageGuid }: ServerPushEvent) => {
      // When the server starts streaming a new message, it first sends a 'new-streamed-message' event
      // this event signals the frontend to start listening for the new message (for the specific context) and discard older streamed messages
      if (event === StreamingEvent.NewStreamedMessage) {
        activeStreams.current = {
          ...activeStreams.current,
          [context]: messageGuid,
        };
        setValue(''); // Clear previous streamed content before new message starts
        return;
      }
      if (event === StreamingEvent.MessageRetry) {
        setValue('');
        isStreaming.current = false;
      }
      if (event === StreamingEvent.SkippedUnsupportedFile) {
        showNotification(message);
      }
      if (activeStreams.current[context] !== messageGuid) {
        return;
      }

      if (event === StreamingEvent.AssistantEvent) {
        if (message === 'thread.message.created') {
          // A new streaming is upon us...
          setValue('');
        } else if (message === 'thread.message.completed') {
          isStreaming.current = false;
        }
      }
      if (event === StreamingEvent.MessageDelta) {
        isStreaming.current = true;
        setValue((current) => streamingText(current, message));
      }
    },
    [showNotification],
  );

  const removeEventEmitterListener = useServerPushNotifications(deltaMessageHandler, feature);

  function stopStreaming() {
    isStreaming.current = false;
    removeEventEmitterListener();
    setValue('');
  }

  return [value, isStreaming.current, stopStreaming];
};
